import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  Download,
  History,
  Loader2,
  Paperclip,
  RotateCcw,
  Save,
  Send,
  Sparkles,
  Trash2,
  Undo2,
} from "lucide-react";
import type {
  DraftDTO,
  DraftRevisionDTO,
  MaterialAttachmentDTO,
  UpdateDraftInput,
} from "@app/shared";
import { api, draftImageUrl, revisionImageUrl } from "../lib/api";
import {
  formatDateTime,
  fromDateTimeLocal,
  toDateTimeLocal,
} from "../lib/format";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { StatusBadge } from "../components/StatusBadge";
import { ImagePreview } from "../components/ImagePreview";
import { DraftPublish } from "../components/DraftPublish";
import { useToast } from "../components/Toast";
import { AttachmentInput } from "../components/AttachmentInput";
import { cn } from "../lib/cn";

interface FormState {
  headline: string;
  subline: string;
  caption: string;
  hashtags: string;
  suggestedTime: string;
  targetPublishLocal: string;
}

function draftToForm(d: DraftDTO): FormState {
  return {
    headline: d.headline ?? "",
    subline: d.subline ?? "",
    caption: d.caption ?? "",
    hashtags: d.hashtags.join(" "),
    suggestedTime: d.suggestedTime ?? "",
    targetPublishLocal: toDateTimeLocal(d.targetPublishAt),
  };
}

function parseHashtags(raw: string): string[] {
  return raw
    .split(/[\s,]+/)
    .map((t) => t.trim().replace(/^#+/, ""))
    .filter((t) => t.length > 0);
}

/** Draft editor: fields, image preview, approve/unschedule, time overrides. */
export function DraftDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const draftQuery = useQuery({
    queryKey: ["drafts", "detail", id],
    queryFn: ({ signal }) => api.draft(id, signal),
    enabled: id.length > 0,
  });

  const [form, setForm] = useState<FormState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [reviseAttachment, setReviseAttachment] =
    useState<MaterialAttachmentDTO | null>(null);

  // Re-seed the form whenever the draft loads/refetches.
  useEffect(() => {
    if (draftQuery.data) setForm(draftToForm(draftQuery.data));
  }, [draftQuery.data]);

  const draft = draftQuery.data;
  const editable =
    draft != null && (draft.status === "draft" || draft.status === "approved");

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const save = useMutation({
    mutationFn: () => {
      if (!form) throw new Error("Form not ready");
      const payload: UpdateDraftInput = {
        headline: form.headline,
        subline: form.subline,
        caption: form.caption,
        hashtags: parseHashtags(form.hashtags),
        // Send as-is, including "" — the API treats an empty string as "clear
        // the suggested time". Coercing to undefined would silently drop the
        // field and prevent the user from blanking it.
        suggestedTime: form.suggestedTime,
        targetPublishAt: fromDateTimeLocal(form.targetPublishLocal),
      };
      return api.updateDraft(id, payload);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(["drafts", "detail", id], updated);
      void queryClient.invalidateQueries({ queryKey: ["drafts"] });
      toast.success("Draft saved");
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Save failed"),
  });

  const approve = useMutation({
    mutationFn: () => api.approveDraft(id),
    onSuccess: (updated) => {
      queryClient.setQueryData(["drafts", "detail", id], updated);
      void queryClient.invalidateQueries({ queryKey: ["drafts"] });
      toast.success("Approved and scheduled");
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Approve failed"),
  });

  // "Post sekarang": approve with the publish time set to now → publishes to
  // Instagram immediately (delay 0) instead of at the scheduled time.
  const postNow = useMutation({
    mutationFn: () => api.approveDraft(id, { now: true }),
    onSuccess: (updated) => {
      queryClient.setQueryData(["drafts", "detail", id], updated);
      void queryClient.invalidateQueries({ queryKey: ["drafts"] });
      toast.success("Diposting sekarang ke Instagram");
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Gagal posting"),
  });

  const unschedule = useMutation({
    mutationFn: () => api.unscheduleDraft(id),
    onSuccess: (updated) => {
      queryClient.setQueryData(["drafts", "detail", id], updated);
      void queryClient.invalidateQueries({ queryKey: ["drafts"] });
      toast.success("Unscheduled — back to draft");
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Unschedule failed"),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteDraft(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["drafts"] });
      toast.success("Draft deleted");
      navigate("/drafts", { replace: true });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Delete failed"),
  });

  // --- AI revision + version history (IG drafts only) ---

  // Refresh both the draft detail and the revision history. The contract keys
  // are ["draft", id] + ["revisions", id]; we also invalidate the existing
  // detail key (["drafts","detail",id]) + the list so the editor re-seeds.
  const refreshDraftAndRevisions = () => {
    void queryClient.invalidateQueries({ queryKey: ["draft", id] });
    void queryClient.invalidateQueries({ queryKey: ["revisions", id] });
    void queryClient.invalidateQueries({ queryKey: ["drafts", "detail", id] });
    void queryClient.invalidateQueries({ queryKey: ["drafts"] });
  };

  const revisionsQuery = useQuery({
    queryKey: ["revisions", id],
    queryFn: ({ signal }) => api.revisions(id, signal),
    enabled: id.length > 0,
  });

  const revise = useMutation({
    mutationFn: () =>
      api.reviseDraft(id, {
        feedback: feedback.trim(),
        attachment: reviseAttachment
          ? {
              id: reviseAttachment.id,
              kind: reviseAttachment.kind,
              ext: reviseAttachment.ext,
            }
          : undefined,
      }),
    onSuccess: () => {
      setFeedback("");
      setReviseAttachment(null);
      toast.success(
        "Revisi diminta — badge ‘Sedang generate’ akan muncul; hasil & versi akan tampil otomatis.",
      );
      // The new version is produced asynchronously (claude + render). Refetch the
      // draft + history a few times as it lands so it appears without a reload.
      [10000, 25000, 45000].forEach((ms) =>
        setTimeout(() => refreshDraftAndRevisions(), ms),
      );
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Revisi gagal"),
  });

  const restore = useMutation({
    mutationFn: (version: number) => api.restoreRevision(id, version),
    onSuccess: (updated) => {
      queryClient.setQueryData(["drafts", "detail", id], updated);
      refreshDraftAndRevisions();
      toast.success(`Versi ${updated.currentVersion} dipulihkan`);
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Pulihkan gagal"),
  });

  if (draftQuery.isLoading || !form) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-teal" />
      </div>
    );
  }

  if (draftQuery.isError || !draft) {
    return (
      <Card>
        <CardContent className="space-y-3 p-8 text-center">
          <p className="text-sm text-coral">Draft not found.</p>
          <Button variant="outline" onClick={() => navigate("/drafts")}>
            Back to drafts
          </Button>
        </CardContent>
      </Card>
    );
  }

  const canUnschedule = draft.status === "scheduled";
  const canApprove = draft.status === "draft" || draft.status === "approved";

  // AI revision + version history are IG-only (mode reguler/special, not
  // articles) and only available while the draft is still editable
  // (status draft/approved).
  const isIgDraft = draft.mode !== "article";
  const showRevisions =
    isIgDraft && (draft.status === "draft" || draft.status === "approved");
  const revisions = revisionsQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link
            to="/drafts"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100"
            aria-label="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-navy">
              Draft detail
            </h1>
            <p className="text-xs text-slate-500">{draft.id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={draft.status} />
          {draft.pillar ? (
            <Badge variant="teal">{draft.pillar}</Badge>
          ) : (
            <Badge variant="coral">Greeting</Badge>
          )}
          <Badge variant="outline">{draft.mode}</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[360px_1fr]">
        {/* Image + metadata column */}
        <div className="space-y-4">
          <ImagePreview src={draftImageUrl(draft)} alt={draft.headline ?? "Draft"} />
          {draft.imagePath ? (
            <a
              href={draftImageUrl(draft)}
              download={`${(draft.headline || "post").replace(/[^\w\s-]+/g, "").trim().slice(0, 50) || "post"}.png`}
              className="flex items-center justify-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-navy"
            >
              <Download className="h-4 w-4" />
              Download gambar
            </a>
          ) : null}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Schedule</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">Target publish</span>
                <span className="text-right font-medium">
                  {formatDateTime(draft.targetPublishAt)}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">Suggested time</span>
                <span className="font-medium">
                  {draft.suggestedTime ?? "—"}
                </span>
              </div>
              {draft.customInstruction ? (
                <div className="space-y-1 border-t border-slate-100 pt-3">
                  <span className="text-slate-500">Instruksi</span>
                  <p className="whitespace-pre-wrap text-xs italic text-slate-600">
                    {draft.customInstruction}
                  </p>
                </div>
              ) : null}
              {draft.externalPostId ? (
                <div className="flex justify-between gap-2">
                  <span className="text-slate-500">IG post id</span>
                  <span className="font-mono text-xs">
                    {draft.externalPostId}
                  </span>
                </div>
              ) : null}
              {draft.errorMsg ? (
                <p className="rounded-md bg-coral/10 p-2 text-xs text-coral">
                  {draft.errorMsg}
                </p>
              ) : null}
            </CardContent>
          </Card>
        </div>

        {/* Edit column */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!editable ? (
                <p className="rounded-md bg-slate-100 p-2 text-xs text-slate-500">
                  This draft is {draft.status} and is read-only. Unschedule to
                  edit again.
                </p>
              ) : null}
              <div className="space-y-1.5">
                <Label htmlFor="headline">Headline</Label>
                <Input
                  id="headline"
                  value={form.headline}
                  disabled={!editable}
                  onChange={(e) => setField("headline", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="subline">Subline</Label>
                <Input
                  id="subline"
                  value={form.subline}
                  disabled={!editable}
                  onChange={(e) => setField("subline", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="caption">Caption</Label>
                <Textarea
                  id="caption"
                  rows={8}
                  value={form.caption}
                  disabled={!editable}
                  onChange={(e) => setField("caption", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="hashtags">Hashtags</Label>
                <Textarea
                  id="hashtags"
                  rows={2}
                  placeholder="space or comma separated, with or without #"
                  value={form.hashtags}
                  disabled={!editable}
                  onChange={(e) => setField("hashtags", e.target.value)}
                />
                <div className="flex flex-wrap gap-1">
                  {parseHashtags(form.hashtags).map((tag) => (
                    <Badge key={tag} variant="secondary">
                      #{tag}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Scheduling overrides</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="suggestedTime">Suggested time (HH:mm)</Label>
                <Input
                  id="suggestedTime"
                  type="time"
                  value={form.suggestedTime}
                  disabled={!editable}
                  onChange={(e) => setField("suggestedTime", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="targetPublish">Target publish at</Label>
                <Input
                  id="targetPublish"
                  type="datetime-local"
                  value={form.targetPublishLocal}
                  disabled={!editable}
                  onChange={(e) =>
                    setField("targetPublishLocal", e.target.value)
                  }
                />
              </div>
            </CardContent>
          </Card>

          {showRevisions ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Sparkles className="h-4 w-4 text-teal" />
                  Revisi dengan AI
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-slate-500">
                  Tulis masukan revisi. AI akan membuat ulang caption dan gambar
                  sebagai versi baru — versi lama tetap tersimpan di riwayat.
                </p>
                <Textarea
                  rows={3}
                  placeholder="Contoh: buat lebih singkat, tonjolkan manfaat utama, hindari istilah teknis…"
                  value={feedback}
                  disabled={revise.isPending}
                  onChange={(e) => setFeedback(e.target.value)}
                />
                <AttachmentInput
                  value={reviseAttachment}
                  onChange={setReviseAttachment}
                  disabled={revise.isPending}
                  hint="Gambar → ganti foto post. PDF/teks → jadi konteks revisi."
                />
                <Button
                  variant="primary"
                  onClick={() => revise.mutate()}
                  disabled={revise.isPending || feedback.trim().length === 0}
                >
                  {revise.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  Revisi dengan AI
                </Button>
              </CardContent>
            </Card>
          ) : null}

          {showRevisions ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                  <History className="h-4 w-4 text-navy" />
                  Riwayat versi
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {revisionsQuery.isLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-teal" />
                  </div>
                ) : revisions.length === 0 ? (
                  <p className="rounded-md bg-slate-100 p-2 text-xs text-slate-500">
                    Belum ada riwayat versi. Versi pertama akan tersimpan saat
                    Anda meminta revisi AI pertama kali.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {[...revisions]
                      .sort((a, b) => b.version - a.version)
                      .map((rev: DraftRevisionDTO) => {
                        const isCurrent =
                          rev.version === draft.currentVersion;
                        const isOriginal =
                          rev.version === 1 || rev.feedback == null;
                        return (
                          <li
                            key={rev.id}
                            className={cn(
                              "flex gap-3 rounded-md border p-3",
                              isCurrent
                                ? "border-teal/40 bg-teal/5"
                                : "border-slate-200",
                            )}
                          >
                            <img
                              src={revisionImageUrl(rev.draftId, rev.version)}
                              alt={`Versi ${rev.version}`}
                              className="h-16 w-[51px] shrink-0 rounded border border-slate-200 object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                            <div className="min-w-0 flex-1 space-y-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <Badge
                                  variant={isCurrent ? "teal" : "secondary"}
                                >
                                  Versi {rev.version}
                                  {isCurrent ? " · current" : ""}
                                </Badge>
                                <span className="text-xs text-slate-400">
                                  {formatDateTime(rev.createdAt)}
                                </span>
                              </div>
                              <p className="text-xs italic text-slate-600">
                                {isOriginal
                                  ? "Asli"
                                  : (rev.feedback ?? "")}
                              </p>
                              {rev.caption ? (
                                <p className="line-clamp-2 text-xs text-slate-500">
                                  {rev.caption}
                                </p>
                              ) : null}
                            </div>
                            <div className="shrink-0 self-center">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => restore.mutate(rev.version)}
                                disabled={isCurrent || restore.isPending}
                              >
                                {restore.isPending &&
                                restore.variables === rev.version ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <RotateCcw className="h-4 w-4" />
                                )}
                                Pulihkan
                              </Button>
                            </div>
                          </li>
                        );
                      })}
                  </ul>
                )}
              </CardContent>
            </Card>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="default"
              onClick={() => save.mutate()}
              disabled={!editable || save.isPending}
            >
              {save.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save
            </Button>

            {canApprove ? (
              <Button
                variant="default"
                onClick={() => approve.mutate()}
                disabled={approve.isPending}
              >
                {approve.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                Approve &amp; schedule
              </Button>
            ) : null}

            {canApprove && isIgDraft ? (
              <Button
                variant="primary"
                onClick={() => postNow.mutate()}
                disabled={postNow.isPending}
              >
                {postNow.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Post sekarang
              </Button>
            ) : null}

            {canUnschedule ? (
              <Button
                variant="outline"
                onClick={() => unschedule.mutate()}
                disabled={unschedule.isPending}
              >
                {unschedule.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Undo2 className="h-4 w-4" />
                )}
                Unschedule
              </Button>
            ) : null}

            {draft.status === "scheduled" ? (
              <span className="inline-flex items-center gap-1 text-xs text-teal">
                <CalendarClock className="h-3.5 w-3.5" />
                Will publish at {formatDateTime(draft.targetPublishAt)}
              </span>
            ) : null}

            <div className="ml-auto">
              <Button
                variant="destructive"
                onClick={() => setConfirmDelete(true)}
                disabled={remove.isPending}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      </div>

      <DraftPublish draftId={id} />

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent onClose={() => setConfirmDelete(false)}>
          <DialogHeader>
            <DialogTitle>Delete this draft?</DialogTitle>
            <DialogDescription>
              This permanently removes the draft and cancels its scheduled job if
              any. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(false)}
              disabled={remove.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              {remove.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
